import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CompetitionsPage.css";

const PSL_COMPETITION_LIST_URL =
  "https://api.timebase.live/psl/competition-list";

interface Competition {
  name: string;
  id: string;
}

type LoadingState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "success"; competitions: Competition[] };

const PSLCompetitionListPage: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadingState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    const fetchCompetitions = async () => {
      try {
        const response = await fetch(PSL_COMPETITION_LIST_URL);

        if (!response.ok) {
          throw new Error(
            `Server returned ${response.status} ${response.statusText}`
          );
        }

        const data: Competition[] = await response.json();

        if (!cancelled) {
          setState({ status: "success", competitions: data });
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : "Unknown error occurred";
          setState({ status: "error", message });
        }
      }
    };

    fetchCompetitions();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = (competitionId: string) => {
    navigate(`/psl/${competitionId}`);
  };

  if (state.status === "loading") {
    return (
      <div className="container">
        <h2>PSL Competition List</h2>
        <p>Loading competitions…</p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="container">
        <h2>PSL Competition List</h2>
        <p style={{ color: "red" }}>
          Failed to load competitions: {state.message}
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      <h2>PSL Competition List</h2>
      <div className="competition-cards">
        {state.competitions.map((competition) => (
          <CompetitionCard
            key={competition.id}
            competition={competition}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  );
};

interface CompetitionCardProps {
  competition: Competition;
  onSelect: (id: string) => void;
}

const CompetitionCard: React.FC<CompetitionCardProps> = ({
  competition,
  onSelect,
}) => (
  <div
    className="competition-card"
    onClick={() => onSelect(competition.id)}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onSelect(competition.id);
    }}
    aria-label={`Select ${competition.name}`}
  >
    <h3>{competition.name}</h3>
    <p style={{ color: "#888", fontSize: "0.85rem" }}>ID: {competition.id}</p>
  </div>
);

export default PSLCompetitionListPage;
